'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';

import Masonry from '@madeinhaus/masonry';
import '@madeinhaus/masonry/dist/index.css';
import cx from 'clsx';

import ButtonCTA from 'components/ui/ButtonCTA';
import Img from 'components/ui/Img';
import Link from 'components/ui/Link';
import Text from 'components/ui/Text';

import TalesFanStoryCard from './TalesFanStoryCard';
import TalesFanStoryFooter from './TalesFanStoryFooter';

import styles from './FanStories.module.scss';

const image1 = {
    url: 'https://images.ctfassets.net/j8tkpy1gjhi5/6zdkvrGSkTLss1okl6HUv2/19f243712dd87959f2244968a718a7e1/your-stories-girl.png',
    width: 653,
    height: 653,
};

const image2 = {
    url: 'https://images.ctfassets.net/j8tkpy1gjhi5/61aIknDPLrDZKNPkY1aykf/fc8a965e34c9f563dc3477ab9d17ac0d/your-stories-child.png',
    width: 652,
    height: 652,
};

const loadMoreIncrement = 12;

const FanStories = ({ stories }) => {
    const containerRef = useRef(null);
    const itemsContainerRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(loadMoreIncrement);
    const [isInView, setIsInView] = useState(false);
    const [itemsInView, setItemsInView] = useState(false);

    const showLoadMore = visibleCount < stories.length;

    const intersection = useIntersection(containerRef, {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.5,
    });

    useEffect(() => {
        intersection && intersection.isIntersecting ? setIsInView(true) : null;
    }, [intersection]);

    const itemsIntersection = useIntersection(itemsContainerRef, {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
    });

    useEffect(() => {
        itemsIntersection && itemsIntersection.isIntersecting
            ? setItemsInView(true)
            : null;
    }, [intersection, itemsIntersection]);

    const handleLoadMoreClick = () => {
        setVisibleCount(prevCount => prevCount + loadMoreIncrement);
    };

    return (
        <section
            id="stories"
            className={cx(styles.root, {
                [styles.isInView]: isInView,
                [styles.itemsInView]: itemsInView,
            })}
        >
            <div className={styles.inner}>
                <div ref={containerRef} className={styles.header}>
                    <div className={styles.lockup}>
                        <Text
                            className={styles.headline}
                            baseTheme="displayMedium"
                            themes={{
                                medium: 'displayLarge',
                                large: 'displayXLarge',
                            }}
                        >
                            Your
                            <br />
                            stories
                        </Text>
                        <Text
                            className={styles.copy}
                            baseTheme="bodySmall"
                            themes={{
                                medium: 'bodyMedium',
                                large: 'bodyLarge',
                            }}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `From small joys to life’s big moments, these are the stories that inspire us to keep moving forward.`,
                                }}
                            />
                        </Text>
                        <ButtonCTA
                            className={styles.cta}
                            style="filled-blue-ocean"
                            text="Add Your Story"
                            link={{
                                slug: 'share-your-tillamook-story',
                            }}
                        />
                        <div className={styles.termsContainer}>
                            <Text
                                className={styles.termsText}
                                baseTheme="bodySmall"
                            >
                                Not all submissions will be featured.{` `}
                                <Link
                                    href={'/share-story/official-terms'}
                                    target="_blank"
                                >
                                    Terms Apply.
                                </Link>
                            </Text>
                        </div>
                    </div>
                    <div className={styles.images}>
                        <Img
                            className={cx(styles.image, styles.image1)}
                            src={image1.url}
                            width={image1?.width}
                            height={image1?.height}
                            fallbackImageWidth={768}
                            alt="Child eating Tillamook ice cream"
                            customSources={[
                                {
                                    breakpoint: 768,
                                    src: image1.url,
                                    imageWidth: 1280,
                                },
                                {
                                    src: image1.url,
                                    imageWidth: 768,
                                },
                            ]}
                        />
                        <Img
                            className={cx(styles.image, styles.image2)}
                            src={image2.url}
                            width={image2?.width}
                            height={image2?.height}
                            fallbackImageWidth={768}
                            alt="Man in kitchen with Tillamook products"
                            customSources={[
                                {
                                    breakpoint: 768,
                                    src: image2.url,
                                    imageWidth: 1280,
                                },
                                {
                                    src: image2.url,
                                    imageWidth: 768,
                                },
                            ]}
                        />
                    </div>
                </div>
                <div
                    className={styles.masonryContainer}
                    ref={itemsContainerRef}
                >
                    <Masonry
                        breakpointCols={{
                            default: 1,
                            768: 2,
                        }}
                        className={styles.masonry}
                        columnClassName={styles.column}
                    >
                        {stories?.length > 0 &&
                            stories
                                .slice(0, visibleCount)
                                .map((story, index) => {
                                    return (
                                        <TalesFanStoryCard
                                            key={index}
                                            order={index}
                                            bodyCopy={story.bodyCopy}
                                            bodyCopyFont={story.bodyCopyFont}
                                            label={`${story.fanName} | ${story.fanLocation}`}
                                            image={story.image}
                                            backgroundColor={
                                                story?.backgroundColor
                                            }
                                            isInView={itemsInView}
                                        />
                                    );
                                })}
                    </Masonry>
                </div>
                <div className={styles.stories}>
                    {showLoadMore && (
                        <div className={styles.loadMore}>
                            <div className={styles.loadMoreInner}>
                                <ButtonCTA
                                    style="filled-blue-ocean"
                                    text="Load More Tales"
                                    onClick={handleLoadMoreClick}
                                />
                            </div>
                        </div>
                    )}
                    <TalesFanStoryFooter
                        showLoadMore={showLoadMore}
                        buttonSlot={
                            <ButtonCTA
                                className={styles.cta}
                                style="filled-blue-ocean"
                                text="Add Your Story"
                                link={{
                                    slug: 'share-your-tillamook-story',
                                }}
                            />
                        }
                    />
                </div>
            </div>
        </section>
    );
};

export default FanStories;
